import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Plus, 
  Search, 
  BarChart3,
  TrendingUp,
  Clock,
  Edit,
  Eye,
  AlertCircle,
  Trash2
} from 'lucide-react'
import { Strategy } from '../types'
import { getUserStrategies, deleteStrategy } from '../lib/api'
import Navigation from '../components/Navigation'
import ConfirmDialog from '../components/ConfirmDialog'
import Toast from '../components/Toast'

export default function StrategiesPage() {
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingStrategy, setDeletingStrategy] = useState<Strategy | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    fetchStrategies()
  }, [])

  // 搜索和筛选变化时重新加载
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStrategies()
    }, 300) // 防抖

    return () => clearTimeout(timeoutId)
  }, [searchTerm, filterType, filterStatus])

  const fetchStrategies = async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await getUserStrategies({
        search: searchTerm || undefined,
        type: filterType !== 'all' ? filterType : undefined,
        limit: 100
      })

      // 应用状态筛选
      let filteredData = data.strategies || []
      if (filterStatus !== 'all') {
        filteredData = filteredData.filter((strategy: any) => strategy.status === filterStatus)
      }

      setStrategies(filteredData)
    } catch (err) {
      console.error('Error fetching strategies:', err)
      setError('加载策略数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const filteredStrategies = strategies.filter(strategy => {
    const matchesSearch = strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         strategy.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || strategy.type === filterType
    const matchesStatus = filterStatus === 'all' || strategy.status === filterStatus
    
    return matchesSearch && matchesType && matchesStatus
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'live':
        return '实盘中'
      case 'test':
        return '验证中'
      default:
        return '草稿'
    }
  }

  const getTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      'trend': '趋势跟踪',
      'grid': '网格交易',
      'mean_reversion': '均值回归',
      'breakout': '突破策略',
      'scalping': '高频交易',
      'other': '其他'
    }
    return typeMap[type] || type
  }

  const handleDeleteStrategy = (strategy: Strategy) => {
    setDeletingStrategy(strategy)
    setShowDeleteConfirm(true)
  }

  const confirmDeleteStrategy = async () => {
    if (!deletingStrategy) return

    try {
      setDeleting(true)
      await deleteStrategy(deletingStrategy.id)
      setToast({ message: '策略已成功删除', type: 'success' })
      fetchStrategies() // 重新加载策略列表
    } catch (error) {
      console.error('Error deleting strategy:', error)
      setToast({ 
        message: error instanceof Error ? error.message : '删除策略失败', 
        type: 'error' 
      })
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
      setDeletingStrategy(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-400"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* 页面头部 */}
        <div className="card mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">策略列表</h1>
            <p className="text-sm sm:text-base text-white/70">管理和查看您的所有交易策略</p>
          </div>
          <Link to="/strategy/new" className="btn-primary flex items-center justify-center sm:justify-start">
            <Plus className="w-4 h-4 mr-2" />
            新建策略
          </Link>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="card mb-4 sm:mb-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* 搜索框 - 始终显示 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
            <input
              type="text"
              placeholder="搜索策略名称或描述..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          {/* 筛选器 - 小屏幕下隐藏，大屏幕显示 */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">所有类型</option>
              <option value="trend">趋势跟踪</option>
              <option value="grid">网格交易</option>
              <option value="mean_reversion">均值回归</option>
              <option value="breakout">突破策略</option>
              <option value="scalping">高频交易</option>
              <option value="other">其他</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">所有状态</option>
              <option value="draft">草稿</option>
              <option value="test">验证中</option>
              <option value="live">实盘中</option>
            </select>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="card mb-4 sm:mb-6">
          <div className="flex items-center p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* 策略列表 */}
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {filteredStrategies.map((strategy) => (
          <div key={strategy.id} className="card hover:bg-white/5 transition-all duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-500/20 rounded-lg flex items-center justify-center border border-violet-500/30">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
                </div>
                <div className="ml-2 sm:ml-3">
                  <h3 className="text-base sm:text-lg font-semibold text-white">{strategy.name}</h3>
                  <p className="text-xs sm:text-sm text-white/60">{strategy.market_symbol}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(strategy.status)}`}>
                {getStatusText(strategy.status)}
              </span>
            </div>

            <p className="text-white/70 text-sm mb-4 line-clamp-2">
              {strategy.description || '暂无描述'}
            </p>

            <div className="flex items-center justify-between text-sm text-white/60 mb-4">
              <span className="flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                {getTypeText(strategy.type)}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {strategy.timeframe}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">
                创建于 {new Date(strategy.created_at).toLocaleDateString('zh-CN')}
              </span>
              <div className="flex space-x-1 sm:space-x-2">
                <Link
                  to={`/strategy/${strategy.id}`}
                  className="flex items-center justify-center p-2 sm:px-3 sm:py-1 text-xs font-medium text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 rounded-md transition-colors"
                  title="查看详情"
                >
                  <Eye className="w-4 h-4 sm:w-3 sm:h-3" />
                  <span className="hidden sm:inline ml-1">查看</span>
                </Link>
                <Link
                  to={`/strategy/${strategy.id}`}
                  className="flex items-center justify-center p-2 sm:px-3 sm:py-1 text-xs font-medium text-white/60 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                  title="编辑策略"
                >
                  <Edit className="w-4 h-4 sm:w-3 sm:h-3" />
                  <span className="hidden sm:inline ml-1">编辑</span>
                </Link>
                <button
                  onClick={() => handleDeleteStrategy(strategy)}
                  className="flex items-center justify-center p-2 sm:px-3 sm:py-1 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors"
                  title="删除策略"
                >
                  <Trash2 className="w-4 h-4 sm:w-3 sm:h-3" />
                  <span className="hidden sm:inline ml-1">删除</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredStrategies.length === 0 && (
        <div className="card text-center">
          <BarChart3 className="mx-auto h-16 w-16 text-white/30 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all' ? '没有找到匹配的策略' : '暂无策略'}
          </h3>
          <p className="text-white/70 mb-6">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
              ? '请尝试调整搜索条件或筛选器'
              : '开始创建您的第一个交易策略'
            }
          </p>
          <Link to="/strategy/new" className="btn-primary flex items-center justify-center">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">新建策略</span>
          </Link>
        </div>
      )}

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="删除策略"
        message={`确定要删除策略 "${deletingStrategy?.name}" 吗？此操作将永久删除该策略及其所有相关数据（参数、回测结果、手动测试记录、研究笔记等），且无法恢复。`}
        confirmText={deleting ? "删除中..." : "删除"}
        cancelText="取消"
        type="danger"
        onConfirm={confirmDeleteStrategy}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setDeletingStrategy(null)
        }}
      />

      {/* 提示信息 */}
      <Toast
        isVisible={!!toast}
        message={toast?.message || ''}
        onClose={() => setToast(null)}
      />
      </main>
    </div>
  )
} 